/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import { Left, Maybe, Right } from 'purify-ts';
import { getStoreBuilder } from 'vuex-typex';
import * as api from '@codegrade/api';
import { assert, defaultdict, itertools } from '@codegrade/utils';
import client from '@/client';
import { INITIAL_COURSES_AMOUNT, MAX_COURSES_PER_LOAD } from '@/constants';
import * as models from '@/models';
import * as utils from '@/utils';
import { CLEAR_RUBRIC_ALL } from '../mutation-types';
import { AssignmentsStore } from './assignments';
import { SiteSettingsStore } from './siteSettings';
import { TenantsStore } from './tenants';
const storeBuilder = getStoreBuilder();
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
const makeInitialState = () => ({
    courses: {},
    permissions: {},
    loaders: {
        singleCourse: {},
        tenantCourses: {},
        firstCourses: null,
        nextCourses: null,
        allCourses: null,
        permissions: null,
    },
    gotAllCourses: false,
});
const moduleBuilder = storeBuilder.module('courses', makeInitialState());
const MAX_COURSES_TO_LOAD = 2500;
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export var CoursesStore;
(function (CoursesStore) {
    /* eslint-disable-next-line no-underscore-dangle, jsdoc/require-jsdoc -- When touching please add documentation. */
    function _getCourse(state) {
        return (courseId) => Maybe.fromNullable(state.courses?.[courseId]);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    CoursesStore.getCourse = moduleBuilder.read(_getCourse, 'getCourse');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    CoursesStore.allCourses = moduleBuilder.read(state => Object.values(state.courses), 'allCourses');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    CoursesStore.sortedCourses = moduleBuilder.read(state => {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        const getVisibleNumber = (course) => {
            switch (course.state) {
                case models.CourseState.visible:
                    return 0;
                case models.CourseState.archived:
                    return 1;
                case models.CourseState.deleted:
                    return 2;
                default:
                    return utils.AssertionError.assertNever(course.state);
            }
        };
        return utils.sortBy(Object.values(state.courses), course => [getVisibleNumber(course), course.createdAt, course.name], { reversePerKey: [false, true, false] });
    }, 'sortedCourses');
    /* eslint-disable-next-line no-underscore-dangle, jsdoc/require-jsdoc -- When touching please add documentation. */
    function _courseTenantIncludes(course, subSearchString) {
        return course.tenant
            .map(tenant => tenant.name.toLocaleLowerCase().includes(subSearchString) ||
            tenant.abbreviatedName.mapOrDefault(abbrev => abbrev.toLocaleLowerCase().includes(subSearchString), false))
            .orDefault(false);
    }
    /* eslint-disable-next-line no-underscore-dangle, jsdoc/require-jsdoc -- When touching please add documentation. */
    function _searchCourses() {
        return (searchString) => {
            const courses = CoursesStore.sortedCourses();
            if (searchString === '' || searchString == null) {
                return courses;
            }
            const filter = searchString.toLocaleLowerCase().split(' ');
            return courses.filter(course => filter.every(sub => course.name.toLocaleLowerCase().includes(sub) ||
                _courseTenantIncludes(course, sub) ||
                course.assignments.some(a => a.name.toLocaleLowerCase().includes(sub))));
        };
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    CoursesStore.searchCourses = moduleBuilder.read(_searchCourses, 'searchCourses');
    // eslint-disable-next-line
    function _getCourseCounts(_) {
        const lookup = new defaultdict.DefaultMap((__) => {
            const byYear = new defaultdict.DefaultMap(() => []);
            return {
                total: [],
                byYear,
            };
        });
        CoursesStore.sortedCourses().forEach(course => {
            const value = lookup.get(course.name);
            value.total.push(course.id);
            value.byYear.get(course.createdAt.clone().local().year()).push(course.id);
        });
        return function getCourseCounts(course) {
            return lookup.get(course.name);
        };
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    CoursesStore.getCourseCounts = moduleBuilder.read(_getCourseCounts, 'getCourseCounts');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    CoursesStore.getTenantCount = moduleBuilder.read(state => new Set(utils.filterMap(Object.values(state.courses), c => c.tenantId)).size, 'getTenantCount');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    CoursesStore.retrievedAllCourses = moduleBuilder.read(state => state.gotAllCourses, 'retrievedAllCourses');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    const commitCourse = moduleBuilder.commit((state, payload) => {
        const { course } = payload;
        utils.vueSet(state.courses, course.id, course);
    }, 'commitCourse');
    const commitCourses = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => {
        const { courses } = payload;
        state.courses = {
            ...state.courses,
            ...courses.reduce((acc, course) => {
                acc[course.id] = course;
                return acc;
            }, {}),
        };
    }, 'commitCourses');
    // This is only exported to be used in tests, don't call this commit from
    // your own code.
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    CoursesStore.commitPermissions = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => {
        state.permissions = Object.freeze({ ...state.permissions, ...payload.permissions });
    }, 'commitPermissions');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    CoursesStore.setPermission = moduleBuilder.commit((state, payload) => {
        const { courseId, permission, value } = payload;
        Maybe.fromNullable(state.permissions[courseId]).ifJust(perms => {
            const newPerms = Object.freeze({ ...perms, [permission]: value });
            CoursesStore.commitPermissions({ permissions: { [courseId]: newPerms } });
        });
    }, 'setPermission');
    const commitClearAll = moduleBuilder.commit(state => {
        Object.assign(state, makeInitialState());
    }, 'commitClearAll');
    const removeSingleCourseLoader = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => {
        const { courseId } = payload;
        utils.vueDelete(state.loaders.singleCourse, courseId);
    }, 'removeSingleCourseLoader');
    const commitSingleCourseLoader = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => {
        const { courseId, value } = payload;
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        const removeAfter = () => removeSingleCourseLoader({ courseId });
        value.then(removeAfter, removeAfter);
        utils.vueSet(state.loaders.singleCourse, courseId, value);
    }, 'commitSingleCourseLoader');
    const removeTenantCourseLoader = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => {
        const { tenantId } = payload;
        utils.vueDelete(state.loaders.tenantCourses, tenantId);
    }, 'removeTenantCourseLoader');
    const commitTenantCourseLoader = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => {
        const { tenantId, value } = payload;
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        const removeAfter = () => removeTenantCourseLoader({ tenantId });
        value.then(removeAfter, removeAfter);
        utils.vueSet(state.loaders.tenantCourses, tenantId, value);
    }, 'commitTenantCourseLoader');
    const removeLoader = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => {
        delete state.loaders[payload.key];
    }, 'removeLoader');
    const commitLoader = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => {
        const { key, value } = payload;
        value.catch(() => {
            removeLoader({ key });
        });
        state.loaders[key] = value;
    }, 'commitLoader');
    const commitPermissionsLoader = moduleBuilder.commit((state, payload) => {
        state.loaders.permissions = payload;
    }, 'commitPermissionsLoader');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    const commitGotAllCourses = moduleBuilder.commit((state, payload) => {
        state.gotAllCourses = payload.value;
    }, 'commitGotAllCourses');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    CoursesStore.loadPermissions = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    ({ state }, payload = {}) => {
        const { force = false } = payload;
        if (state.loaders.permissions == null || force) {
            commitPermissionsLoader(client.permission
                .getAll({ type: 'course' })
                .then(response => response.unsafeCoerce())
                .then(({ cgResult }) => CoursesStore.commitPermissions({
                // We expect a record indexable by number in the rest of the app but
                // the api returns a record indexable by string. These have the same
                // underlying representation, though, so we can safely cast the
                // result to a record indexable by number.
                permissions: cgResult,
            })));
        }
        return state.loaders.permissions;
    }, 'loadPermissions');
    // eslint-disable-next-line
    function _getPermissionsForCourse(state) {
        return (courseId) => Maybe.fromNullable(state.permissions[courseId]);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    CoursesStore.getPermissionsForCourse = moduleBuilder.read(_getPermissionsForCourse, 'getPermissionsForCourse');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    CoursesStore.commitPermission = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => {
        const { courseId, perm, value } = payload;
        const perms = state.permissions[courseId];
        if (perms == null) {
            throw new Error(`Cannot update permission for course ${courseId}, no permissions were found. Available courses: ${Object.keys(state.permissions).join(', ')}`);
        }
        state.permissions = Object.freeze({
            ...state.permissions,
            [courseId]: Object.freeze({
                ...perms,
                [perm]: value,
            }),
        });
    }, 'commitPermission');
    /** Function to add courses to the store. */
    CoursesStore.addCourses = moduleBuilder.dispatch(({ state }, payload) => {
        const { courses, force } = payload;
        const allAssignments = courses.reduce((acc, course) => {
            course.assignments.forEach(assignment => {
                acc[assignment.id] = models.Assignment.fromApiServerData(assignment);
            });
            return acc;
        }, {});
        const courseGetter = _getCourse(state);
        courses.forEach(course => {
            courseGetter(course.id)
                .ifJust(oldCourse => {
                oldCourse.assignmentIds.forEach(assignmentId => {
                    if (!utils.hasAttr(allAssignments, assignmentId)) {
                        AssignmentsStore.commitRemoveAssignment({ assignmentId });
                    }
                });
            })
                .ifNothing(() => {
                // This is a new course. We will probably display it and thus need the
                // tenant settings and the tenant permissions.
                course.tenantId.ifJust(tenantId => {
                    utils.loadBackgroundApis(SiteSettingsStore.loadTenantSettings({
                        tenantId,
                        catchAllErrors: true,
                    }));
                });
            });
        });
        AssignmentsStore.commitAssignments({ assignmentsById: allAssignments });
        commitCourses({
            courses: courses.map(models.Course.fromApiServerData),
        });
        if (CoursesStore.allCourses().some(c => c.showTenant)) {
            const tenantIds = new Set(Maybe.catMaybes(CoursesStore.allCourses().map(course => course.tenantId)));
            Promise.all(itertools.map(tenantIds, id => TenantsStore.loadSingleTenant({ tenantId: id, force })));
        }
    }, 'addCourses');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    CoursesStore.addCourse = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (_, payload) => {
        CoursesStore.addCourses({ courses: [payload.course], force: false });
    }, 'addCourse');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    CoursesStore.loadSingleCourse = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    async ({ state }, payload) => {
        const { courseId, force = false } = payload;
        await Promise.all([state.loaders.firstCourses, state.loaders.allCourses]);
        const hasCourse = _getCourse(state)(courseId).isJust();
        const isLoading = state.loaders.singleCourse[courseId] != null;
        if (force || (!hasCourse && !isLoading)) {
            const loader = Promise.all([
                client.course.get({ courseId }).then(response => response.unsafeCoerce()),
                CoursesStore.loadPermissions({ force }),
            ]).then(([{ cgResult }]) => CoursesStore.addCourse({ course: cgResult }));
            commitSingleCourseLoader({ courseId, value: loader });
        }
        await state.loaders.singleCourse[courseId];
    }, 'loadSingleCourse');
    /**
     * Assert that all courses are extended.
     *
     * @param courses The courses to check.
     * @returns A `Left` if any of the courses is an extended, or a `Right`
     * containing the course list if all of them are.
     */
    const allAsExtended = (courses) => {
        for (const course of courses) {
            if (!(course instanceof api.ExtendedCourse)) {
                return Left(new assert.AssertionError(`Course ${course.id} is not extended.`));
            }
        }
        return Right(courses);
    };
    /**
     * Load the first `INITIAL_COURSES_AMOUNT` courses.
     *
     * @param payload The arguments to this dispatch.
     * @returns An `EitherAsync` that resolves when the request has finished.
     */
    CoursesStore.loadFirstCourses = moduleBuilder.dispatch(async ({ state }, payload) => {
        const { force = false } = payload ?? {};
        const permLoader = CoursesStore.loadPermissions({ force });
        const didLoad = state.loaders.firstCourses != null;
        const didLoadAll = state.loaders.allCourses != null;
        if ((!didLoad && !didLoadAll) || force) {
            if (force) {
                await permLoader;
                await state.loaders.firstCourses;
            }
            const courseLoader = client.course.getAll({
                offset: 0,
                limit: INITIAL_COURSES_AMOUNT,
            });
            commitLoader({
                key: 'firstCourses',
                value: Promise.all([courseLoader, permLoader])
                    .then(([response]) => response.chain(res => allAsExtended(res.cgResult)).unsafeCoerce())
                    .then(courses => {
                    CoursesStore.addCourses({ courses, force });
                    // If we got less than our limit there are no more courses.
                    commitGotAllCourses({
                        value: state.gotAllCourses || courses.length < INITIAL_COURSES_AMOUNT,
                    });
                }),
            });
        }
        else if (didLoadAll) {
            return state.loaders.allCourses;
        }
        return state.loaders.firstCourses;
    }, 'loadFirstCourses');
    /**
     * Load the next `amount` of courses into the list of courses.
     *
     * @param payload The arguments to this dispatch.
     * @returns An `EitherAsync` that resolves when the request has finished.
     */
    CoursesStore.loadNextCourses = moduleBuilder.dispatch(async ({ state }, payload) => {
        const { force = false, amount = MAX_COURSES_TO_LOAD } = payload ?? {};
        const permLoader = CoursesStore.loadPermissions({ force });
        const didLoadFirst = state.loaders.firstCourses != null;
        if (didLoadFirst || force) {
            await Promise.all([permLoader, state.loaders.firstCourses]);
        }
        const offset = state.courses == null ? 0 : Object.keys(state.courses).length - 1;
        const limit = Math.min(amount, MAX_COURSES_PER_LOAD);
        const courseLoader = client.course.getAll({
            offset,
            limit,
        });
        commitLoader({
            key: 'nextCourses',
            value: Promise.all([courseLoader, permLoader])
                .then(([response]) => response.chain(res => allAsExtended(res.cgResult)).unsafeCoerce())
                .then(courses => {
                CoursesStore.addCourses({ courses, force });
                commitGotAllCourses({
                    value: state.gotAllCourses || courses.length < limit,
                });
                return courses;
            }),
        });
        return state.loaders.nextCourses;
    }, 'loadNextCourses');
    /** @deprecated Load MAX_COURSES_TO_LOAD amount of courses in one go. */
    CoursesStore.loadAllCourses = moduleBuilder.dispatch(async ({ state }, payload) => {
        const { force = false } = payload ?? {};
        const permLoader = CoursesStore.loadPermissions({ force });
        if (state.loaders.allCourses == null || force) {
            const courseLoader = client.course.getAll({
                limit: MAX_COURSES_TO_LOAD,
                offset: 0,
            });
            commitLoader({
                key: 'allCourses',
                value: Promise.all([courseLoader, permLoader])
                    .then(([response]) => response.chain(res => allAsExtended(res.cgResult)).unsafeCoerce())
                    .then(courses => {
                    CoursesStore.addCourses({ courses, force });
                    commitGotAllCourses({ value: true });
                }),
            });
        }
        return state.loaders.allCourses;
    }, 'loadAllCourses');
    /** Load all of the courses for a specific tenant, using a tenantId. */
    CoursesStore.loadTenantCourses = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    async ({ state }, payload) => {
        const { tenantId, force = false } = payload;
        const tenantCourses = state.loaders.tenantCourses[tenantId];
        if (tenantCourses != null && !force) {
            return tenantCourses;
        }
        const permLoader = CoursesStore.loadPermissions({ force });
        const courseLoader = client.tenant.getCourses({
            tenantId,
        });
        commitTenantCourseLoader({
            tenantId,
            value: Promise.all([courseLoader, permLoader])
                .then(([response]) => response.unsafeCoerce())
                .then(({ cgResult }) => {
                CoursesStore.addCourses({ courses: cgResult, force });
            }),
        });
        return state.loaders.tenantCourses[tenantId];
    }, 'loadTenantCourses');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    CoursesStore.reloadCourses = moduleBuilder.dispatch((ctx, payload) => {
        const { fullReload } = payload;
        commitClearAll();
        AssignmentsStore.commitClearAll();
        ctx.commit(`rubrics/${CLEAR_RUBRIC_ALL}`, null, { root: true });
        if (fullReload) {
            return CoursesStore.loadAllCourses({ force: false });
        }
        else {
            return CoursesStore.loadFirstCourses({ force: false });
        }
    }, 'reloadCourses');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    CoursesStore.updateCourse = moduleBuilder.dispatch(async ({ state }, 
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    payload) => {
        const { courseId, courseProps } = payload;
        await CoursesStore.loadSingleCourse({ courseId });
        _getCourse(state)(courseId).caseOf({
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            Just(course) {
                commitCourse({ course: course.update(courseProps) });
            },
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            Nothing() {
                throw new ReferenceError(`Could not find course ${courseId}`);
            },
        });
    }, 'updateCourse');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    CoursesStore.createCourse = moduleBuilder.dispatch(async (_, payload) => {
        const response = await client.course
            .create({ requestBody: payload })
            .then(res => res.unsafeCoerce());
        await CoursesStore.loadPermissions({ force: true });
        CoursesStore.addCourse({ course: response.cgResult });
        return response;
    }, 'createCourse');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    CoursesStore.patchCourse = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    async (_, payload) => {
        const response = await client.course
            .patch({
            courseId: payload.courseId,
            requestBody: payload.data,
        })
            .then(res => res.unsafeCoerce());
        CoursesStore.addCourse({ course: response.cgResult });
        return response;
    }, 'patchCourse');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    CoursesStore.commitAssignment = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => {
        _getCourse(state)(payload.courseId).caseOf({
            Just: course => {
                commitCourse({ course: course.addAssignment(payload.assignment) });
            },
            Nothing: () => {
                CoursesStore.reloadCourses({ fullReload: true });
            },
        });
    }, 'commitAssignment');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    CoursesStore.copyCourse = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (_, payload) => client.course
        .importInto({
        intoCourseId: payload.into.id,
        requestBody: {
            fromCourseId: payload.from.id,
        },
    })
        .ifRight(({ body: course }) => {
        const assignments = course.assignments.map(assig => models.Assignment.fromApiServerData(assig));
        AssignmentsStore.commitAssignments({
            assignmentsById: utils.makeLookupMapping(assignments, 'id'),
        });
        commitCourse({
            course: models.Course.fromApiServerData(course),
        });
    }), 'copyCourse');
    utils.signal.AFTER_USER_LOGGED_OUT.register(() => {
        commitClearAll();
    });
})(CoursesStore || (CoursesStore = {}));
